import { apiRequest, apiRequestWithAuth } from "@/api";
import config from "@/config";
import MOJ_APP_VERSION from "@/config/version.properties";
import constants from "@/constants";
import { SSR_EVENTS_ID } from "@/lib/common/constants";
import store from "@/stores/profile";
import { getUserData, isLocalHost, nUserAgent } from "@/utils";
import { getCreatorId } from "@/utils/gamificationUtils";
import { getSafeSearchParam } from "@mohalla-tech/xss-safeguard";
import isbot from "isbot";
import { get } from "svelte/store";
import { extractRedirectUrl, isMobile } from "../utils/helpers/utils";
import EVENTS from "./events";
const {
  MOJ_CLIENT,
  MOJ_USER_LANGUAGE,
  DEMO_CREDENTIAL,
  STAGING_DEMO_CREDENTIAL,
} = constants;

const eventNameMapping = {
  HomeOpened: 3,
  FollowButton: 451,
  LikeButton: 452,
  CommentButton: 453,
  [EVENTS.SHARE_BUTTON]: 454,
  AudioLabel: 455,
  BottomNavTapped: 456,
  HandleTapped: 457,
  [EVENTS.VIDEO_PLAY]: 79, // tableName: video_play
  [EVENTS.VIDEO_PLAY_START]: 409, // tableName: video_play_start_event
  MfcStatusApiFail: 963,
  DownloadAppButton: 1235,
  clickingInSidepaneweb: 1277,
  selectionOfTenantWeb: 1278,
  loginScreenLandingweb: 1279,
  [EVENTS.PAYMENT_EVENTS.OPEN_RECHARGE_OPTIONS]: 780, // tableName : open_recharge_options
  selectRechargeOptions: 783,
  clickedBuy: 1226,
  [EVENTS.PAYMENT_EVENTS.SELECT_PAYMENT_METHOD]: 1015, // tableName: select_Payment_Method
  [EVENTS.PAYMENT_EVENTS.RECHARGE_WALLET]: 784, // tableName: recharge_wallet
  [EVENTS.PAYMENT_EVENTS.OPEN_PAYMENT_PAGE]: 1231, // tableName: open_payment_page
  [EVENTS.PAYMENT_EVENTS.RECHARGE_STATUS]: 785, // tableName: recharge_status
  GamificationTrophiesPageActions: 1512526558,
  MojttMigrationNudge: 1345032363,
  [EVENTS.PAYMENT_EVENTS.PAYMENT_INITIATION_EVENT]: 282007200, // tableName: payment_initiation_event
  PwaAuthenticatedHomeOpened: 408520111,
  TrophyCardOpened: 73861619, // tableName: trophy_card_opened
  TrophyPageOpened: 338278176, // tableName: trophy_page_opened
  EditTrophyOpened: 1935365287, // tableName: edit_trophy_opened
  TrophyDetailsEdited: 266867591, // tableName: trophy_details_edited
  TrophyShared: 1058323515, // tableName: trophy_shared
  [EVENTS.PWA_STATIC_INSTALL_VIEW]: 1589617173, //pwa_static_install_view
  [EVENTS.PWA_STATIC_INSTALL_CLICK]: 246624696, //pwa_static_install_click
  [EVENTS.PWA_BOTTOMBAR_CLICK]: 421458495, // tableName: pwa_bottombar_click
  [EVENTS.PWA_BOTTOMBAR_NUDGE_ACTION]: 510770898, // tableName: pwa_bottombar_nudge_action
  [EVENTS.PWA_SHARE_NUDGE_ACTION]: 2097185124, // tableName: pwa_share_nudge_action
  [EVENTS.MFC_EVENTS.MFC_JOURNEY_ZERO_STATE_PAGE_OPENED]: 1101302447, // tableName: mfc_journey_zero_state_page_opened
  [EVENTS.MFC_EVENTS.LIVE_ACCESS_APPLY_NOW_CLICKED]: 1313759873, // tableName: live_access_apply_now_clicked
  [EVENTS.MFC_EVENTS.MFC_SELECT_APPLY_NOW_CLICKED]: 338222387, //tableName: mfc_select_apply_now_clicked
  [EVENTS.MFC_EVENTS.MFC_SELECT_KNOW_MORE_CLICKED]: 417947274, //tableName: mfc_select_know_more_clicked
  [EVENTS.MFC_EVENTS.CREATOR_JOURNEY_FLOW_EVENTS]: 308343008, //tableName: journey_web_events
  [EVENTS.SSR_EVENTS]: SSR_EVENTS_ID, //tableName: ssr_events
  [EVENTS.BOT_USER_EVENTS]: 513622946, //tableName: pwa_bot_events
};
const { eventsServiceUrl } = config;
/**
 * Function to track various kind of events
 * @param {string} eventName
 * @param {object} eventPayload
 * @returns {object}
 */

const trackEvents = (
  eventName,
  eventPayload = {},
  clientType = MOJ_CLIENT,
  hasAuthorizationToken = false
) => {
  const { ...payload } = eventPayload;
  const { deviceId, userId, passCode } = get(store);
  const hrefLink = location.pathname.includes("redirect")
    ? extractRedirectUrl(location.href, "from=")
    : location.href;
  let language;
  const { appVersion } = getUserData();
  const creatorId = getCreatorId();
  const botUserId =
    process.env.ACTIVE_ENV === "STAGING" ||
    process.env.ACTIVE_ENV === "DEVELOPMENT"
      ? STAGING_DEMO_CREDENTIAL["x-sharechat-userid"]
      : DEMO_CREDENTIAL["x-sharechat-userid"];
  const isBotUser = isbot(nUserAgent()) || userId === botUserId;
  const eventTableId = isBotUser
    ? eventNameMapping[EVENTS.BOT_USER_EVENTS]
    : eventNameMapping[eventName] || null;

  if (typeof window !== "undefined") {
    language = window.location?.search
      ? getSafeSearchParam("language")
      : MOJ_USER_LANGUAGE;
    if (window.Android) {
      language = window.Android.get("userLang");
    }
  }
  const header = {
    "Content-Type": "application/json",
    "X-SHARECHAT-USERID": userId,
    "X-SHARECHAT-SECRET": passCode,
    "APP-VERSION": MOJ_APP_VERSION,
    "CLIENT-TYPE": clientType,
    "PACKAGE-NAME": "in.mohalla.video",
    "DEVICE-ID": deviceId,
  };
  const requestPayload = {
    appVersion: hasAuthorizationToken ? appVersion : MOJ_APP_VERSION,
    bn: "broker1",
    client: MOJ_CLIENT,
    language,
    deviceId,
    message: {
      batchRequest: 1,
      requestArray: [
        {
          clientType: MOJ_CLIENT,
          i: eventTableId,
          t: Date.now(),
          // uuid: deviceId, // do we need to change this to uuidv4() and add deviceId? seems like we arent using this anywhere
          url: hrefLink || "",
          user_agent: nUserAgent(),
          userId: hasAuthorizationToken ? creatorId : userId,
          deviceId,
          ...(isBotUser ? { eventPayload: JSON.stringify(payload) } : payload),
        },
      ],
    },
    passCode,
    userId: hasAuthorizationToken ? creatorId : userId,
  };

  if (!isLocalHost()) {
    if (hasAuthorizationToken) {
      return apiRequestWithAuth(
        window.fetch,
        `${eventsServiceUrl}/requestType16`,
        "POST",
        requestPayload,
        {}
      );
    } else {
      return apiRequest(
        window.fetch,
        `${eventsServiceUrl}/requestType16`,
        "POST",
        requestPayload,
        header
      ).catch(() => {});
    }
  }
};

export const getCommonEventData = () => {
  const { userId, tenant } = get(store);
  const time = Date.now();
  return {
    t: time,
    time,
    ntp_eventRecordTime: time,
    ntp_eventDispatchTime: time,
    networkType:
      window?.navigator?.connection?.effectiveType ||
      window?.navigator?.connection?.type,
    clientType: isMobile() ? "mWeb" : "web",
    type: isMobile() ? "mWeb" : "web",
    distinct_id: userId,
    memberId: userId,
    tenant,
    payment_gateway: "RAZORPAY_CUSTOM",
  };
};
export default trackEvents;
